// Mock data for fmi.dev portfolio clone
export const PROFILE = {
  handle: "fmi.dev",
  fullName: "Firza Miftahul Ilmi",
  role: "Full-stack developer & AI researcher",
  email: "firzailmidja@gmail.com",
  photo: "/profile-photo.png",
  taglineParts: [
    "Engineering serious software at the intersection of ",
    { highlight: "Informatics" },
    ", ",
    { highlight: "Applied AI" },
    ", and modern web systems.",
  ],
};

export const NAV = [
  { label: "Works", href: "/works" },
  { label: "Articles", href: "/articles" },
  { label: "About", href: "/about" },
  { label: "Contact", href: "/contact" },
];

export const SKILLS = [
  { id: "01", title: "Frontend", items: ["React.js", "Next.js (App Router)", "Three.js", "Tailwind"] },
  { id: "02", title: "Backend", items: ["Node.js", "REST / tRPC", "MongoDB", "PostgreSQL"] },
  { id: "03", title: "Applied AI", items: ["Gemini", "Claude", "OpenAI", "RAG / Agents"] },
  { id: "04", title: "Research", items: ["Systematic Literature Review", "Curation Pipelines", "Evaluation"] },
];

export const PROJECTS = [
  {
    id: "01",
    name: "Vistara",
    type: "Web3 Infrastructure Landing",
    desc: "Modular landing architecture for a Web3 infrastructure stack, built with a token-driven design system, fluid responsive breakpoints, and framer-motion micro-interactions.",
    stack: ["Next.js", "Tailwind CSS", "Framer Motion", "TypeScript"],
    repoUrl: "https://github.com/Maverick-list/vistara",
    liveUrl: "https://vistara.network",
    capability: "Frontend Architecture",
  },
  {
    id: "02",
    name: "MavecodeCourse",
    type: "Full-Stack Learning Platform",
    desc: "Interactive curriculum delivery system featuring user authentication, modular lesson progression, persistent tracking, and custom course content authoring.",
    stack: ["Next.js", "Node.js", "MongoDB", "Tailwind CSS"],
    repoUrl: "https://github.com/Maverick-list/mavecode-course",
    liveUrl: "https://mavecode.my.id",
    capability: "Full-Stack Systems",
  },
  {
    id: "03",
    name: "MaveMentor AI",
    type: "Applied AI / RAG Pipeline",
    desc: "Conversational learning assistant surfacing context-aware guidance through retrieval-augmented generation (RAG), FastAPI middleware, and vector similarity search.",
    stack: ["Python", "FastAPI", "Gemini API", "Vector Embeddings"],
    repoUrl: "https://github.com/Maverick-list/mavementor-ai",
    liveUrl: null,
    capability: "Applied AI / LLM",
  },
  {
    id: "04",
    name: "AI Content Curation",
    type: "Informatics & SLR Research",
    desc: "Academic Systematic Literature Review (SLR) research tool designed to automate literature search indexing, duplicate detection, and structured metadata extraction.",
    stack: ["Python", "React", "SLR Methodology", "Informatics"],
    repoUrl: "https://github.com/Maverick-list/ai-content-curation",
    liveUrl: null,
    capability: "Research Pipelines",
  },
];

export const ARTICLES = [
  {
    slug: "design-systems-decisions",
    date: "2026-02-09",
    category: "Design",
    tags: ["Design Systems", "Architecture", "Tokens", "CSS"],
    title: "Design systems are decisions, not components",
    excerpt: "Components are the artifact. The real value of a design system is the decision pipeline behind it — the constraints you accept, the affordances you reject, and the contracts that survive a redesign.",
    readMin: 4,
    toc: [
      { id: "illusion-of-components", title: "The Illusion of Components" },
      { id: "tokens-as-policy", title: "Tokens as Architectural Policy" },
      { id: "the-token-contract", title: "The Token Contract in Practice" },
      { id: "what-survives-redesign", title: "What Survives a Redesign" },
    ],
    sections: [
      {
        id: "illusion-of-components",
        heading: "The Illusion of Components",
        content: [
          "Most teams mistake a component library for a design system. They assemble forty variants of a button, nine modal behaviors, and six dropdown styles in Figma, export them to React, and declare victory. Within six months, product managers demand custom one-offs, engineers duplicate styles in ad-hoc utility classes, and the library becomes an unmaintainable graveyard of forgotten flags.",
          "Components are merely downstream artifacts. The real value of a design system is the decision pipeline behind it — the constraints you accept, the affordances you reject, and the shared vocabulary that allows engineering and design to move at velocity without continuous renegotiation.",
        ],
      },
      {
        id: "tokens-as-policy",
        heading: "Tokens as Architectural Policy",
        content: [
          "When you treat design tokens as policy rather than cosmetic variables, a Figma diff stops being a visual review and becomes a governance process. Every token is an explicit answer to a concrete product question.",
          "Instead of arguing whether a surface border should be `#27272a` or `#3f3f46`, tokens express functional intent: `--color-surface-border`, `--color-surface-subtle`, and `--focus-ring`. When these semantics are enforced across design files and codebase linters, cosmetic drift is caught before it enters pull requests.",
        ],
        quote: "Treating tokens as policy turns Figma diff into governance. Every variable becomes an explicit answer to a product question.",
      },
      {
        id: "the-token-contract",
        heading: "The Token Contract in Practice",
        content: [
          "In production systems, tokens must be defined at the boundary layer so that changes cascade deterministically across web, mobile, and third-party embeds. Here is the contract pattern implemented across our UI foundation:",
        ],
        code: {
          language: "css",
          title: "theme-tokens.css",
          snippet: `:root {
  /* Intent-driven semantic tokens */
  --bg-canvas: hsl(240 10% 3.9%);
  --bg-card: hsl(240 10% 4.8%);
  --border-subtle: hsl(240 6% 14%);
  --accent-primary: hsl(263 70% 50%);
  --text-primary: hsl(0 0% 98%);
  --text-muted: hsl(240 5% 65%);
  --focus-ring: hsl(263 80% 65%);

  /* Strict typography and rhythm scale */
  --font-mono: 'JetBrains Mono', monospace;
  --tracking-wide: 0.22em;
}`,
        },
      },
      {
        id: "what-survives-redesign",
        heading: "What Survives a Redesign",
        content: [
          "What dies first when scope collapses? When launch deadlines loom and features are pruned, whatever remains tells you the objective truth about your system.",
          "If removing an animation breaks the layout, your layout was brittle. If swapping a color theme requires editing sixty components individually, your tokens were superficial. A mature design system is defined not by how much UI it produces, but by how easily it can be adapted or discarded without leaving behind architectural debris.",
        ],
      },
    ],
  },
  {
    slug: "informatics-craft-software",
    date: "2026-01-15",
    category: "Research",
    tags: ["Informatics", "Software Engineering", "SLR", "Methodology"],
    title: "Informatics and the craft of software",
    excerpt: "Notes on bridging academic informatics research with production engineering — and why both halves matter.",
    readMin: 4,
    toc: [
      { id: "academic-gap", title: "The Academic-Engineering Disconnect" },
      { id: "empirical-discipline", title: "Empirical Discipline in Frontend Systems" },
      { id: "the-friction-line", title: "Building at the Friction Line" },
    ],
    sections: [
      {
        id: "academic-gap",
        heading: "The Academic-Engineering Disconnect",
        content: [
          "In modern software development, academic informatics and commercial software engineering frequently inhabit disconnected universes. Academic literature develops rigorous ontological models, formal taxonomy structures, and statistical evaluation frameworks — yet implementations are often relegated to unmaintained Python notebooks.",
          "Conversely, commercial web development moves at astonishing speed with modern toolchains, TypeScript compilers, and distributed clouds — but frequently lacks conceptual models, reinventing ad-hoc schemas that collapse as soon as dataset complexity scales.",
        ],
      },
      {
        id: "empirical-discipline",
        heading: "Empirical Discipline in Frontend Systems",
        content: [
          "Informatics provides the conceptual models, empirical methods, and evaluation criteria; software engineering instills operational discipline, type safety, and deployment rigor.",
          "Applying Systematic Literature Review (SLR) principles to software architecture means treating your state and data flow as a verifiable evidence chain. You define explicit inclusion criteria for data models, eliminate duplicate records through canonical key hashing, and track the lineage of state changes with deterministic purity.",
        ],
        quote: "The most compelling technical work sits directly at this friction line — translating theoretical abstractions into shipping interfaces without compromising intellectual depth or architectural integrity.",
      },
      {
        id: "the-friction-line",
        heading: "Building at the Friction Line",
        content: [
          "When you bridge both worlds, code ceases to be just imperative instructions and becomes a structured medium for knowledge. Tools like our SLR Curation pipeline or Vistara's layout engine succeed precisely because they marry formal models with clean component lifecycles.",
          "Engineers who master both informatics rigor and frontend craft don't just write faster code — they build systems that remain intelligible long after the original authors have left the project.",
        ],
      },
    ],
  },
  {
    slug: "rag-pipelines-in-production",
    date: "2025-12-02",
    category: "Applied AI",
    tags: ["Applied AI", "RAG", "Embeddings", "FastAPI"],
    title: "RAG pipelines in production",
    excerpt: "Retrieval is not a model problem — it's a data and evaluation problem. Here is how I treat it.",
    readMin: 5,
    toc: [
      { id: "retrieval-not-model", title: "Retrieval Is Not a Model Problem" },
      { id: "chunking-and-drift", title: "Chunking Strategy & Semantic Drift" },
      { id: "hybrid-fusion", title: "Hybrid Retrieval with Reciprocal Rank Fusion" },
      { id: "ground-truth-evaluation", title: "Ground-Truth Evaluation as CI" },
    ],
    sections: [
      {
        id: "retrieval-not-model",
        heading: "Retrieval Is Not a Model Problem",
        content: [
          "When developers encounter hallucinations or incomplete answers in a retrieval-augmented generation (RAG) system, their default impulse is often to upgrade the generation model: swap GPT-3.5 for GPT-4, or increase the parameter count. In production environments, this almost never solves the underlying failure.",
          "Most RAG failures are evaluation and retrieval failures. If your retrieval mechanism injects irrelevant chunks, truncates critical sentences, or fails on out-of-vocabulary acronyms, the most sophisticated LLM in existence can only rationalize bad input. You cannot generate your way out of poor retrieval.",
        ],
        quote: "Most RAG failures are evaluation failures. If you cannot measure groundedness reliably, you cannot improve it.",
      },
      {
        id: "chunking-and-drift",
        heading: "Chunking Strategy & Semantic Drift",
        content: [
          "Naïve fixed-character chunking (e.g., slicing raw strings every 500 characters with 50-character overlap) is the leading cause of semantic degradation. A chunk sliced mid-sentence loses subject referents; a table split across chunks produces garbled tabular reasoning.",
          "Production RAG architectures require document-aware, hierarchical chunking: Markdown headers, AST boundaries in code, and recursive section splitting preserve semantic locality. Chunks should carry rich metadata headers linking them back to the canonical parent document and section ID.",
        ],
      },
      {
        id: "hybrid-fusion",
        heading: "Hybrid Retrieval with Reciprocal Rank Fusion",
        content: [
          "Pure dense vector search (bi-encoder cosine similarity) is powerful for conceptual matches, but notoriously fragile for exact SKU numbers, error codes, and niche technical terms. The production standard is hybrid retrieval combining dense semantic embeddings with sparse lexical indexing (BM25):",
        ],
        code: {
          language: "python",
          title: "hybrid_retriever.py",
          snippet: `def reciprocal_rank_fusion(dense_ranks, sparse_ranks, k=60):
    """
    Combines dense embedding ranks and sparse BM25 scores
    into a balanced score invariant to score distribution differences.
    """
    scores = {}
    for rank, doc_id in enumerate(dense_ranks):
        scores[doc_id] = scores.get(doc_id, 0.0) + (1.0 / (k + rank + 1))
    for rank, doc_id in enumerate(sparse_ranks):
        scores[doc_id] = scores.get(doc_id, 0.0) + (1.0 / (k + rank + 1))
    return sorted(scores.items(), key=lambda x: x[1], reverse=True)`,
        },
      },
      {
        id: "ground-truth-evaluation",
        heading: "Ground-Truth Evaluation as CI",
        content: [
          "Before shipping any prompt or index modification, we benchmark against synthetic ground-truth test sets containing verified query-document-answer triplets. We measure three orthogonal metrics:",
          "1. Context Relevance: Did the retrieved documents actually address the query?",
          "2. Groundedness / Faithfulness: Is every assertion in the response directly supported by the context?",
          "3. Answer Completeness: Did the model answer all sub-questions posed by the user?",
          "When evaluation runs as a continuous integration check, quality regressions are stopped before production deployment.",
        ],
      },
    ],
  },
];

export const NOTIFICATIONS = [
  { id: 1, title: "New comment on Vistara", time: "2m" },
  { id: 2, title: "Article saved as draft", time: "1h" },
  { id: 3, title: "Theme updated to dark", time: "1d" },
];

export const SOCIALS = [
  { label: "Github", icon: "github", href: "#" },
  { label: "Linkedin", icon: "linkedin", href: "#" },
  { label: "Instagram", icon: "instagram", href: "#" },
  { label: "X", icon: "x", href: "#" },
  { label: "Email", icon: "mail", href: "mailto:firzailmidja@gmail.com" },
  { label: "Youtube", icon: "youtube", href: "#" },
  { label: "Tiktok", icon: "tiktok", href: "#" },
];

// 53 weeks * 7 days github contributions mock
export function makeContributions() {
  const weeks = 53; const days = 7;
  const arr = [];
  for (let w = 0; w < weeks; w++) {
    const col = [];
    for (let d = 0; d < days; d++) {
      // bias toward 0-1, occasional spikes
      const r = Math.random();
      let level = 0;
      if (r > 0.55) level = 1;
      if (r > 0.78) level = 2;
      if (r > 0.9) level = 3;
      if (r > 0.97) level = 4;
      col.push(level);
    }
    arr.push(col);
  }
  return arr;
}

export const CADENCE = [
  { m: "Jan", commits: 82, prs: 12, reviews: 6 },
  { m: "Feb", commits: 100, prs: 18, reviews: 9 },
  { m: "Mar", commits: 70, prs: 9, reviews: 5 },
  { m: "Apr", commits: 138, prs: 22, reviews: 11 },
  { m: "May", commits: 96, prs: 14, reviews: 8 },
  { m: "Jun", commits: 124, prs: 20, reviews: 9 },
  { m: "Jul", commits: 88, prs: 11, reviews: 6 },
  { m: "Aug", commits: 150, prs: 24, reviews: 12 },
  { m: "Sep", commits: 110, prs: 16, reviews: 8 },
];

export const ADMIN_STATS = [
  { k: "Visitors", v: "12,408", delta: "+8.2%" },
  { k: "Articles", v: "24", delta: "+2" },
  { k: "Projects", v: "12", delta: "+1" },
  { k: "Avg. Read", v: "3m 12s", delta: "+0.4%" },
];
